import React from 'react';
import { Link } from 'react-router-dom';
import { User, ExternalLink, Ruler, Weight, Activity, Calendar } from 'lucide-react';
import { Client } from '@/types/client';
import { formatWeight, formatHeight } from '@/utils/unit-conversion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getClientDetailsPath } from '@/components/clients/ClientNameLink';

interface ClientDetailsBadgeProps {
    clientId: string;
    clientDetails: Client | null | undefined;
    clientDisplayName?: string;
    variant?: 'default' | 'compact' | 'card';
    className?: string;
    planDuration?: number;
    status?: string;
}

const ClientDetailsBadge: React.FC<ClientDetailsBadgeProps> = ({
    clientId,
    clientDetails,
    clientDisplayName,
    variant = 'default',
    className = '',
    planDuration,
    status,
}) => {
    if (!clientId) return null;

    const clientDetailsPath = getClientDetailsPath(clientId);
    const editableClientDetailsPath = `${clientDetailsPath}?mode=edit`;

    const displayName =
        clientDisplayName ||
        clientDetails?.fullName ||
        (clientDetails?.firstName && clientDetails?.lastName
            ? `${clientDetails.firstName} ${clientDetails.lastName}`
            : '');

    if (!displayName) return null;

    const measurementSystem = (clientDetails?.preferredMeasurementSystem || 'metric') as 'metric' | 'imperial';
    const formattedHeight = clientDetails?.heightCm ? formatHeight(clientDetails.heightCm, measurementSystem) : null;
    const formattedWeight = clientDetails?.weightKg ? formatWeight(clientDetails.weightKg, measurementSystem) : null;
    const bmr = clientDetails?.bmr;

    const hasMetrics = formattedHeight || formattedWeight || bmr;

    // Card variant - includes the full card with stats
    if (variant === 'card') {
        return (
            <Card className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border border-blue-200/50 shadow-sm">
                <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                        {/* Client Details Section */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                                <Link
                                    to={editableClientDetailsPath}
                                    className="inline-flex items-center gap-2.5 rounded-lg border border-blue-200 bg-white/80 backdrop-blur-sm px-4 py-2.5 text-blue-700 hover:bg-blue-50 hover:border-blue-300 hover:shadow-sm transition-all text-sm font-medium group"
                                    title="View and edit client details"
                                >
                                    {clientDetails && (
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-sm group-hover:shadow-md transition-shadow">
                                            {clientDetails.firstName?.[0]}{clientDetails.lastName?.[0]}
                                        </div>
                                    )}
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-gray-900 truncate max-w-[12rem] underline-offset-2 group-hover:underline sm:max-w-xs">{displayName}</span>
                                        {clientDetails?.email && (
                                            <span className="text-xs text-gray-500 truncate max-w-[12rem] sm:max-w-xs">{clientDetails.email}</span>
                                        )}
                                    </div>
                                    <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                </Link>
                                {hasMetrics && (
                                    <div className="flex items-center gap-3 text-xs text-gray-600 bg-white/60 backdrop-blur-sm px-3 py-2 rounded-lg border border-gray-200/50">
                                        {formattedHeight && (
                                            <span className="flex items-center gap-1.5 font-medium">
                                                <Ruler className="h-3.5 w-3.5 text-gray-400" />
                                                {formattedHeight}
                                            </span>
                                        )}
                                        {formattedWeight && (
                                            <span className="flex items-center gap-1.5 font-medium">
                                                <Weight className="h-3.5 w-3.5 text-gray-400" />
                                                {formattedWeight}
                                            </span>
                                        )}
                                        {bmr && (
                                            <span className="flex items-center gap-1.5 font-medium">
                                                <Activity className="h-3.5 w-3.5 text-gray-400" />
                                                BMR: {bmr.toFixed(0)} kcal
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Stats Section */}
                        <div className="flex items-center gap-4 flex-wrap">
                            {planDuration !== undefined && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <span className="font-medium text-gray-900">{planDuration} days</span>
                                </div>
                            )}
                            {clientDetails?.dateOfBirth && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <User className="h-4 w-4 text-gray-400" />
                                    <span className="font-medium text-gray-900">
                                        {new Date().getFullYear() - new Date(clientDetails.dateOfBirth).getFullYear()} yrs
                                    </span>
                                </div>
                            )}
                            {clientDetails?.gender && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <span className="font-medium text-gray-900 capitalize">{clientDetails.gender}</span>
                                </div>
                            )}
                            {status && (
                                <Badge 
                                    variant="outline" 
                                    className={`font-medium ${
                                        status === 'Finalized' || status === 'finalized' || status === 'active'
                                            ? 'bg-green-50 text-green-700 border-green-300'
                                            : 'bg-yellow-50 text-yellow-700 border-yellow-300'
                                    }`}
                                >
                                    {status}
                                </Badge>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-3 flex-wrap ${className}`}>
        <Link
          to={editableClientDetailsPath}
          className="inline-flex items-center gap-2.5 rounded-lg border border-blue-200 bg-white/80 backdrop-blur-sm px-4 py-2.5 text-blue-700 hover:bg-blue-50 hover:border-blue-300 hover:shadow-sm transition-all text-sm font-medium group"
          title="View and edit client details"
        >
          {clientDetails && (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-sm group-hover:shadow-md transition-shadow">
              {clientDetails.firstName?.[0]}{clientDetails.lastName?.[0]}
            </div>
          )}
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900 truncate max-w-[12rem] underline-offset-2 group-hover:underline sm:max-w-xs">{displayName}</span>
            {clientDetails?.email && (
              <span className="text-xs text-gray-500 truncate max-w-[12rem] sm:max-w-xs">{clientDetails.email}</span>
            )}
          </div>
          <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
        </Link>
        {hasMetrics && (
          <div className="flex items-center gap-3 text-xs text-gray-600 bg-white/60 backdrop-blur-sm px-3 py-2 rounded-lg border border-gray-200/50">
            {formattedHeight && (
              <span className="flex items-center gap-1.5 font-medium">
                <Ruler className="h-3.5 w-3.5 text-gray-400" />
                {formattedHeight}
              </span>
            )}
            {formattedWeight && (
              <span className="flex items-center gap-1.5 font-medium">
                <Weight className="h-3.5 w-3.5 text-gray-400" />
                {formattedWeight}
              </span>
            )}
            {bmr && (
              <span className="flex items-center gap-1.5 font-medium">
                <Activity className="h-3.5 w-3.5 text-gray-400" />
                BMR: {bmr.toFixed(0)} kcal
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

    return (
        <div className={`inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white shadow-sm px-4 py-2.5 hover:shadow-md transition-shadow ${className}`}>
            <Link
                to={editableClientDetailsPath}
                className="flex items-center gap-2 text-blue-700 hover:text-blue-800 transition-colors group"
                title="View and edit client details"
            >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors">
                    <User className="h-4 w-4 text-blue-600" />
                </div>
                <span className="font-semibold text-sm truncate max-w-[12rem] underline-offset-2 group-hover:underline sm:max-w-xs">{displayName}</span>
                <ExternalLink className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
            </Link>

            {hasMetrics && (
                <div className="h-6 w-px bg-gray-300 mx-1" />
            )}

            {hasMetrics && (
                <div className="flex items-center gap-4 text-xs text-gray-600">
                    {formattedHeight && (
                        <div className="flex items-center gap-1.5">
                            <Ruler className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-medium">{formattedHeight}</span>
                        </div>
                    )}
                    {formattedWeight && (
                        <div className="flex items-center gap-1.5">
                            <Weight className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-medium">{formattedWeight}</span>
                        </div>
                    )}
                    {bmr && (
                        <div className="flex items-center gap-1.5">
                            <Activity className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-medium">BMR: {bmr.toFixed(0)} kcal</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientDetailsBadge;
